import { z } from '@hono/zod-openapi';
import type { CheckResult, IExif } from '../types';
import { MAX_WEBHOOK_URL_LENGTH, WebhookUrlSchema } from '../utils/http-url';
import { isValidVinCode, normalizeVin } from '../utils/vin';
import { ConfigurationSchema } from './configuration';

export const ExifDataSchema = z.record(z.string(), z.any()).openapi({
  description:
    'EXIF metadata from piexif-ts library - contains IFD structure with 0th, Exif, GPS sections. Also includes metadata field with raw PhotoMetadata.',
  example: {
    '0th': {
      '271': 'Transpordiamet',
      '305': 'Vehicle Inspection System v1.0',
      '306': '2025:01:17 14:30:00',
    },
    Exif: {
      '36867': '2025:01:17 14:30:00',
      '36868': '2025:01:17 14:30:00',
    },
    GPS: {
      '0': [2, 3, 0, 0],
      '1': 'N',
      '2': [
        [59, 1],
        [26, 1],
        [15000, 1000],
      ],
      '3': 'E',
      '4': [
        [24, 1],
        [45, 1],
        [12960, 1000],
      ],
    },
    metadata: {
      latitude: 59.437,
      longitude: 24.753,
      capturedAt: '2025-01-17T14:30:00.000Z',
    },
  },
}) satisfies z.ZodType<IExif>;

export const CheckResultSchema = z.object({
  type: z.enum(['dimensions', 'quality', 'gps', 'vin', 'exif', 'is_car']).openapi({
    example: 'quality',
    description: 'Type of validation check performed',
  }),
  value: z.union([z.boolean(), z.string(), z.number(), z.null()]).openapi({
    example: true,
    description:
      'Check result value (boolean for is_car, string for VIN, number for quality/sharpness, null for VIN not found)',
  }),
  confidence: z.number().min(0).max(100).optional().openapi({
    example: 95,
    description: 'Optional confidence score (0-100) for AI-based checks',
  }),
  passed: z.boolean().openapi({
    example: true,
    description: 'Whether the check passed validation. Required in both requests and responses.',
  }),
  allow_bypass: z.boolean().optional().openapi({
    example: false,
    description: 'Whether this check can be bypassed if it fails',
  }),
  error: z.string().optional().openapi({
    example: 'VEHICLE_DETECTION_FAILED',
    description:
      'Error code for localized error messages when check fails (e.g., VEHICLE_DETECTION_FAILED, VIN_FORMAT_INVALID, VIN_EXTRACTION_FAILED, VIN_MISMATCH)',
  }),
  message: z.string().optional().openapi({
    example: 'Failed to detect vehicle in image',
    description: 'Translated error message in the session language (if check failed)',
  }),
}) satisfies z.ZodType<CheckResult>;

export const PhotoSchema = z.object({
  id: z.string().openapi({
    example: '123e4567-e89b-12d3-a456-426614174001',
  }),
  sessionId: z.string().openapi({
    example: '123e4567-e89b-12d3-a456-426614174000',
  }),
  stepId: z.string().openapi({
    example: 'front-diagonal',
    description: 'ID of the photo step this photo fulfills',
  }),
  state: z.enum(['draft', 'confirmed']).openapi({
    example: 'draft',
    description: 'State of the photo - draft (pending confirmation) or confirmed',
  }),
  r2Path: z.string().openapi({
    example: 'sessions/sess_abc123/photo_xyz.jpg',
    description: 'Path to the photo in R2 storage',
  }),
  r2Url: z.string().url().openapi({
    example: 'https://r2.tram.pildista.ee/sessions/sess_abc123/photo_xyz.jpg',
    description: 'Full public URL to access the photo in R2 storage',
  }),
  createdAt: z.date().or(z.string()).openapi({
    example: '2025-01-06T12:00:00Z',
  }),
  exifData: ExifDataSchema,
  checks: z.array(CheckResultSchema).openapi({
    description: 'Validation checks performed on this photo',
  }),
});

// ============================================================================
// Session Schemas
// ============================================================================

export const SessionSchema = z
  .object({
    id: z.string(),
    externalId: z.string(),
    vehicleCategory: z.string(),
    state: z.enum(['active', 'completed', 'expired']),
    url: z.string().url().openapi({
      description:
        'Share URL for the capture app. On POST create this is fixed from SESSION_BASE_URL and optional language; on GET it is rebuilt live from SESSION_BASE_URL and Accept-Language.',
    }),
    createdAt: z.date().or(z.string()),
    expiredAt: z.date().or(z.string()).nullable(),
    completedAt: z.date().or(z.string()).nullable(),
    vinCode: z.string().nullable().openapi({
      example: 'WVWZZZ3CZHE123456',
      description:
        'Vehicle Identification Number supplied at session create. Null on sessions created before this field existed.',
    }),
    regNumber: z.string().nullable().openapi({
      example: '123ABC',
      description: 'Optional vehicle registration plate supplied at session create. Null when omitted.',
    }),
    pendingDrafts: z.array(PhotoSchema).optional().openapi({
      description:
        'Latest unconfirmed draft per unfinished step that failed a bypassable VIN or vehicle check. Restores the confirm dialog after a reload. Confirmed photos stay in `photos`.',
    }),
    configuration: ConfigurationSchema,
    photos: z.array(PhotoSchema).optional(),
  })
  .openapi('Session');

// ============================================================================
// Session List Schema (GET /sessions, admin-console login token only)
// ============================================================================

export const SessionListItemSchema = z
  .object({
    id: z.string(),
    externalId: z.string(),
    vehicleCategory: z.string(),
    state: z.enum(['active', 'completed', 'expired']),
    vinCode: z.string().nullable(),
    regNumber: z.string().nullable(),
    createdAt: z.date().or(z.string()),
    expiresAt: z.date().or(z.string()),
    completedAt: z.date().or(z.string()).nullable(),
    configurationName: z.string(),
    labels: z.array(z.string()).openapi({ description: 'Labels of the configuration snapshot' }),
    stepCount: z.number().int(),
    photoCount: z.number().int().openapi({ description: 'Confirmed photos' }),
    hasWebhook: z.boolean().openapi({ description: 'Whether a webhookUrl was set. The URL itself is never returned.' }),
    webhookAttempts: z.number().int(),
    webhookSentAt: z.date().or(z.string()).nullable(),
    webhookFailedAt: z.date().or(z.string()).nullable(),
    webhookLastError: z.string().nullable(),
  })
  .openapi('SessionListItem');

/** POST /sessions create response — includes QR SVG generated at create time (not stored). */
export const SessionCreateResponseSchema = SessionSchema.extend({
  qrSvg: z.string().openapi({
    description: 'SVG markup of a QR code encoding the session URL (not stored in the database)',
    example: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">...</svg>',
  }),
}).openapi('SessionCreateResponse');

// ============================================================================
// Photo Creation Schema
// ============================================================================

export const PhotoCreateSchema = z
  .object({
    stepId: z.string().openapi({
      example: 'front-diagonal',
      description: 'ID of the photo step this photo fulfills',
    }),
    r2Path: z.string().openapi({
      example: 'sessions/sess_abc123/photo_xyz.jpg',
      description: 'Path where the photo was uploaded in R2 storage',
    }),
    exifData: ExifDataSchema,
    checks: z.array(CheckResultSchema).openapi({
      description: 'Validation checks performed on this photo',
    }),
  })
  .openapi('PhotoCreate');

// ============================================================================
// Request/Response Schemas
// ============================================================================

// POST /sessions - Create session request
export const SessionCreateSchema = z
  .object({
    externalId: z.string().openapi({
      example: 'QUEUE-12345',
      description: 'External identifier from registration system',
    }),
    configurationId: z.string().openapi({
      example: 'abc123',
      description: 'ID of the configuration to use for this session',
    }),
    language: z.enum(['en', 'et', 'ru']).optional().openapi({
      example: 'en',
      description:
        'Optional language preference for the session (en, et, or ru). When provided, will be appended to the session URL as a query parameter.',
    }),
    vinCode: z
      .string()
      .trim()
      .min(1)
      .transform((value) => normalizeVin(value))
      .refine((value) => isValidVinCode(value), {
        message: 'Must be 1-17 characters: Latin letters A-Z and digits 0-9',
      })
      .openapi({
        example: 'WVWZZZ3CZHE123456',
        description:
          'Vehicle Identification Number (required). 1-17 characters, Latin letters A-Z and digits 0-9 ' +
          '(no diacritics, spaces or symbols). 17 characters is a standard ISO 3779 VIN; shorter values cover pre-1981 and ' +
          'Soviet-era vehicles that carry a chassis/frame number instead. Stored uppercase. A configuration with `strictVinCode` ' +
          '(DIRE) requires 4-17 characters without I, O or Q.',
      }),
    regNumber: z
      .string()
      .trim()
      .min(1)
      .max(16)
      .transform((value) => value.toUpperCase())
      .optional()
      .openapi({
        example: '123ABC',
        description: 'Optional vehicle registration plate. Stored uppercase. Omitted or null when not provided.',
      }),
    webhookUrl: WebhookUrlSchema.optional().openapi({
      example: 'https://dire.example/webhooks/photos',
      description: `Optional per-session webhook URL. Completion notifications are sent only to this URL. Not returned on GET or in webhook payloads. Must be an https URL on a public hostname (loopback, private and link-local addresses are rejected), at most ${MAX_WEBHOOK_URL_LENGTH} characters. Redirects are not followed. To have the webhook authenticate with HTTP Basic auth, embed percent-encoded credentials (\`https://user:password@host/path\`): they are sent as an \`Authorization: Basic\` header and removed from the requested URL.`,
    }),
  })
  .openapi('SessionCreate');

// POST /sessions/:sessionId/upload/presign - Request presigned URL
export const PresignSchema = z
  .object({
    r2Path: z.string().openapi({
      example: 'sessions/sess_abc123/photo_xyz.jpg',
      description: 'Desired path for the file in R2 storage',
    }),
  })
  .openapi('Presign');

export const PresignedUrlSchema = z
  .object({
    uploadURL: z.string().url().openapi({
      example: 'https://storage.example.com/presigned-url',
      description: 'Presigned URL for uploading the file (valid for 5 minutes)',
    }),
    key: z.string().openapi({
      example: 'sessions/sess_abc123/photo_xyz.jpg',
      description: 'The R2 path/key for the file',
    }),
    expiresIn: z.number().int().openapi({
      example: 300,
      description: 'Number of seconds until the presigned URL expires',
    }),
    publicUrl: z.string().url().optional().openapi({
      example: 'https://pub-1234567890abcdef.r2.dev/illustrations/image.jpg',
      description: 'Public URL for accessing the uploaded file (if R2_PUBLIC_DOMAIN is configured)',
    }),
  })
  .openapi('PresignedUrl');

// Session ID parameter schema
export const SessionIdParamSchema = z.object({
  sessionId: z.string().openapi({
    param: { name: 'sessionId', in: 'path' },
    example: '123e4567-e89b-12d3-a456-426614174000',
  }),
});

// Error schema — used for handler errors and OpenAPI 4xx docs.
// Validation failures (OpenAPI/Zod defaultHook) may also include `details`.
export const ValidationErrorDetailSchema = z
  .object({
    path: z.string().openapi({
      example: 'steps.0.illustration_url',
      description: 'Dotted path to the invalid field',
    }),
    message: z.string().openapi({
      example: 'Invalid URL',
      description: 'Human-readable validation message for this field',
    }),
    code: z.string().openapi({
      example: 'invalid_format',
      description: 'Zod issue code',
    }),
  })
  .openapi('ValidationErrorDetail');

export const ErrorSchema = z
  .object({
    error: z.string().openapi({
      example: 'VALIDATION_ERROR',
      description: 'Machine-readable error code (e.g. VALIDATION_ERROR, VEHICLE_CATEGORY_NOT_FOUND)',
    }),
    message: z.string().openapi({
      example: 'steps.0.illustration_url: Invalid URL',
      description: 'Human-readable summary (may be English technical text for validation errors)',
    }),
    details: z.array(ValidationErrorDetailSchema).optional().openapi({
      description:
        'Optional per-field validation issues. Present on OpenAPI/Zod request validation failures (error=VALIDATION_ERROR).',
    }),
  })
  .openapi('Error');

// ============================================================================
// Type Exports
// ============================================================================

export type Photo = z.infer<typeof PhotoSchema>;
export type Session = z.infer<typeof SessionSchema>;
export type SessionListItem = z.infer<typeof SessionListItemSchema>;
export type SessionCreateResponse = z.infer<typeof SessionCreateResponseSchema>;
export type PhotoCreate = z.infer<typeof PhotoCreateSchema>;
export type SessionCreate = z.infer<typeof SessionCreateSchema>;
export type Presign = z.infer<typeof PresignSchema>;
export type PresignedUrl = z.infer<typeof PresignedUrlSchema>;
